import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AppShell } from './components/AppShell.js';
import { AssistantUtterance, LearnerBubble } from './components/Utterance.js';
import { Composer, type ComposerState } from './components/Composer.js';
import { UnderstandingPanel } from './components/UnderstandingPanel.js';
import { SessionList } from './components/SessionList.js';
import { Notice } from './components/Notice.js';
import { QuotaChip } from './components/QuotaChip.js';
import { Button } from './components/Button.js';
import styles from './App.module.css';
import { ErrorBanner } from './components/ErrorBanner.js';
import { PhaseIndicator } from './components/PhaseIndicator.js';
import { Text } from './components/Text.js';
import { Thinking } from './components/Thinking.js';
import { Icon } from './components/Icon.js';
import { ICONS } from './icons/index.js';
import { EKUMARU_AVATAR, XLABO_SYMBOL } from './assets.js';
import type { CharacterState } from './components/CharacterAvatar.js';
import { createSession, listSessions, readSession, sendTurn, type ApiConfig, type ChatMessage, type PanelRow, type SessionSummary } from './lib/session.js';

/**
 * **チャット画面**（M16）。
 *
 * ## §4.4 の時間軸をそのまま落とす
 *
 * | 時刻 | 画面 |
 * | --- | --- |
 * | 0.0s | **入力欄は最初から打てる**（打てないストレスを与えない） |
 * | 4〜6s | probe が届く |
 * | 10〜55s（実測 78〜102s） | **何も出さない**。**ここに工程名を出すと隠蔽が壊れる** |
 * | `graph_ready` | 送信ボタンが通常に戻る。**保留中の入力があれば自動送信**。パネルが中身を持つ |
 *
 * **学習者が知覚する待ち時間は約 6 秒。この体験を壊さないことが
 * UI 側の最重要要件で、送信保留の見せ方がその全部である**（§4.4）。
 *
 * ## `version` を持ち回る
 *
 * **`expectedVersion` が食い違うと `duplicate`** が返る（M15 実測で 0.20 秒、
 * **LLM を呼ぶ前に弾かれる**ので課金されない）。
 * **`duplicate` を受け取ったら、サーバが返した現在の版に合わせる。**
 */

export interface AppProps {
  readonly api: ApiConfig;
  /** 開発モードのコスト表示（D26） */
  readonly showCost?: boolean;
}

let seq = 0;
const nextId = (): string => `m${++seq}`;

/**
 * **`DialogueAct` の種類 → 表情**（§4.6 規約 6）。
 *
 * **アプリが決定的に決める。LLM に選ばせない**（INV-2 の系。
 * D86 決定4「`strength` はアプリが決める」と同じ形）。
 *
 * **正解への近さを表情で伝えない**（規約 7）— **これは INV-5 の漏洩経路**であり、
 * **`displayLabel` の境界検査では捕まらない**。
 * だから写像の左辺は**すべて AI 自身の行為**であって、
 * **学習者の答えの当たり具合ではない**。
 *
 * **`celebrating` は `CLOSE_SESSION` だけ**（D17 の `COMPLETED`。**そこでは既に開示済み**）。
 * **`thinking` はここに現れない** — 表情ではなく待ちの合図であり、
 * **不可視フェーズに出してはならない**（§2.2 / §4.4）ので `Thinking` 側が持つ。
 */
export const CHARACTER_BY_ACT: Readonly<Record<string, CharacterState>> = {
  ASK_OPENING_PROBE: 'asking',
  ASK_DIAGNOSTIC: 'asking',
  ASK_EXPLAIN_BACK: 'asking',
  CLARIFY: 'asking',
  DEFER_ANSWER_REQUEST: 'no-answer',
  DECLINE_OUT_OF_SCOPE: 'no-answer',
  CLOSE_SESSION: 'celebrating',
};

export function App({ api }: AppProps): ReactNode {
  const [messages, setMessages] = useState<readonly ChatMessage[]>([]);
  const [panel, setPanel] = useState<readonly PanelRow[]>([]);
  const [sessions, setSessions] = useState<readonly SessionSummary[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [version, setVersion] = useState(0);
  const [draft, setDraft] = useState('');
  /** **問題名の材料**（モックのスレッドのバー）。1 問 = 1 セッション */
  const [problemText, setProblemText] = useState<string | undefined>(undefined);
  /**
   * **いま何をしているか**（D132）。
   *
   * **`startedAt` を一緒に持つ** — §4.5 が再構築ターンについて
   * 「**工程名 + 経過秒**」と定めており、**62〜101 秒の不可視フェーズには
   * それ以上に要る**（数字が動いていれば、止まっていないことが分かる）。
   *
   * **経過は送信からの通算にする** — 工程ごとにリセットすると、
   * **数字が戻った瞬間が「やり直した」ように読める**。
   */
  const [phase, setPhase] = useState<{ label: string; startedAt: number } | undefined>(undefined);
  /** **経過秒を 1 秒ごとに進める**（`phase` が無い間は動かさない） */
  const [now, setNow] = useState(0);
  const [error, setError] = useState<{ kind: 'recoverable' | 'fatal'; message: string } | undefined>();
  const [busy, setBusy] = useState(false);

  /**
   * **不可視フェーズ中に押された送信を保留する**（§2.7 / §4.4）。
   * **`graph_ready` で自動送信する。**
   */
  const pendingSubmit = useRef<string | undefined>(undefined);
  const bootstrapping = useRef(false);
  /**
   * **保留していることを画面に出すかどうか**（§2.7）。
   *
   * **`bootstrapping` とは別の状態である。**
   * §2.7 は「**押すと**『準備中』と表示され」と書いており、
   * **押す前は通常の見た目でなければならない**。
   *
   * **不可視フェーズの間ずっと「準備中」を出すと、
   * 何もしていないのに「何かが保留されている」と広告することになり、
   * §2.2 の隠蔽がそこで壊れる**（実際にそう作って、
   * ブラウザで「回り続けて入力できない」と読まれた）。
   */
  const [held, setHeld] = useState(false);
  /** **保留が 15 秒を超えたか**（§4.4 の `phase(waiting)`） */
  const [waitingLong, setWaitingLong] = useState(false);

  /**
   * **§4.4: 保留 15 秒超 →「もう少しかかります」。**
   *
   * **隠蔽が終わるのは学習者が送信を押した瞬間である** —
   * そこから先は**待たされていることを本人が知っている**ので、
   * **黙っている方が壊れて見える**。
   *
   * **工程名は出さない**（§4.4: **ここに工程名を出すと隠蔽が壊れる**）。
   * **「もう少しかかります」は工程名ではない。**
   */
  useEffect(() => {
    if (!held) {
      setWaitingLong(false);
      return;
    }
    const t = setTimeout(() => setWaitingLong(true), 15_000);
    return () => clearTimeout(t);
  }, [held]);

  /**
   * **経過秒を進める**（D132）。
   *
   * **`phase` が無い間は `setInterval` を張らない** — 張りっぱなしにすると
   * **会話を読んでいるだけの時間も毎秒再描画する**。
   */
  useEffect(() => {
    if (phase === undefined) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    void listSessions(api).then(setSessions);
  }, [api]);

  const append = useCallback((m: ChatMessage) => {
    setMessages((prev) => [...prev, m]);
  }, []);

  const handlers = useCallback(
    (onDone?: (v: number) => void) => ({
      onUtterance: (text: string, actType: string | undefined) => {
        /**
         * **`phase` はここで消さない**（D132）。
         *
         * probe が届いた直後に `phase(solving)` が来る。
         * **消すと 1 フレームだけ工程名が消えて点滅する。**
         * **工程名を畳むのは `done` と `graph_ready`** —
         * **どちらも「その待ちが終わった」ことを意味する**。
         */
        append({ id: nextId(), role: 'assistant' as const, text, ...(actType === undefined ? {} : { actType }) });
      },
      onGraphReady: (sid: string, labels: readonly string[]) => {
        setSessionId(sid);
        /**
         * **URL にセッションを残す**（M16。**実使用でリロードすると消えた**）。
         *
         * **`history.replaceState` を使う** — `pushState` にすると
         * **戻るボタンで「セッションが無い状態」に戻る**（学習者には
         * 何が起きたか分からない）。
         *
         * **`SSE は演出、真実は DynamoDB`** なので、
         * **URL さえ残っていれば読み直せる。**
         */
        try {
          history.replaceState({}, '', `?s=${encodeURIComponent(sid)}`);
        } catch {
          /** 失敗しても対話は続く（URL が残らないだけ） */
        }
        bootstrapping.current = false;
        /** **待ちが終わったので工程名を畳む**（D132） */
        setPhase(undefined);
        /**
         * **境界を通ったラベルだけ**が届く（M6）。
         * **状態は engine が持つ**ので、作成直後はすべて `unknown`。
         * **UI は並べ替えない**（D121。API が `conceptKey` 昇順で返す）。
         */
        setPanel(labels.map((l, i) => ({ id: `r${i + 1}`, displayLabel: l, status: 'unknown' as const })));
        /** **保留していた入力を自動送信する**（§2.7） */
        const queued = pendingSubmit.current;
        pendingSubmit.current = undefined;
        setHeld(false);
        if (queued !== undefined) void submitTurn(sid, queued, 0, true);
      },
      /**
       * **いま何をしているかを出す**（D132。**言い換えない**）。
       * **中身は出さない** — 出せるのは「何をしているか」だけである
       * （T1 の出力は `sealedSolution` そのもので、ラベルは境界を通る前）。
       */
      onPhase: (label: string) => {
        /** **開始時刻は引き継ぐ** — 工程が変わっても経過秒は戻さない */
        setPhase((prev) => ({ label, startedAt: prev?.startedAt ?? Date.now() }));
      },
      /**
       * **理解状態パネルを毎ターン更新する**（D11。M16 で配線した）。
       *
       * **それまでパネルは作成時の一覧を出したまま、最後まで一度も動かなかった** —
       * engine は状態を持っていたのに、**画面へ届く経路が 1 本も無かった**。
       * **UI は並べ替えない**（D121。サーバが並べた順のまま入れる）。
       */
      onState: (rows: readonly PanelRow[]) => {
        setPanel(rows);
      },
      onDegraded: (detail: string) => {
        /** **縮退を黙らせない**（D114）。ただし**会話の流れは止めない** */
        // eslint-disable-next-line no-console
        console.warn('[ua] degraded', detail);
      },
      onUsage: () => {
        /* 開発モードの表示は M16 の後半で足す（D26） */
      },
      onDone: (v: number) => {
        setVersion(v);
        setBusy(false);
        setPhase(undefined);
        onDone?.(v);
      },
      onError: (reason: string, currentVersion?: number) => {
        setBusy(false);
        setPhase(undefined);
        if (reason === 'duplicate') {
          /**
           * **二重送信は失敗ではない。**
           * **サーバが返した現在の版に合わせて続ける**
           * （**その版のターンは既に処理済み**である）。
           */
          if (currentVersion !== undefined) setVersion(currentVersion);
          setError({ kind: 'recoverable', message: 'この送信はすでに処理されています。' });
          return;
        }
        setError({
          kind: reason === 'not_found' ? 'fatal' : 'recoverable',
          message:
            reason === 'not_found'
              ? 'このセッションは見つかりません。'
              : '応答を最後まで受け取れませんでした。',
        });
      },
    }),
    [append],
  );

  const submitTurn = useCallback(
    async (sid: string, utterance: string, expectedVersion: number, alreadyShown = false) => {
      setBusy(true);
      setError(undefined);
      /**
       * **保留した発話は押した時点で既に会話へ出している**ので、
       * ここで二度出さない（**同じ発言が 2 つ並ぶ**）。
       */
      if (!alreadyShown) append({ id: nextId(), role: 'learner', text: utterance });
      const out = await sendTurn(api, { sessionId: sid, utterance, expectedVersion }, handlers());
      if (out.kind === 'incomplete') {
        /**
         * **SSE は演出、真実は DynamoDB**（§9 / §4.7）。
         * **サーバ側のターンは完了している可能性がある**ので、
         * **失敗ではなく「読み直しが要る」として伝える**。
         */
        setBusy(false);
        setError({ kind: 'recoverable', message: '応答が途中で切れました。' });
      }
      if (out.kind === 'http_error') {
        setBusy(false);
        setError({ kind: 'fatal', message: `通信に失敗しました（${out.status}）。` });
      }
      if (out.kind === 'network_error') {
        setBusy(false);
        setError({ kind: 'recoverable', message: `接続できませんでした: ${out.message}` });
      }
    },
    [api, append, handlers],
  );

  const start = useCallback(
    async (utterance: string) => {
      setBusy(true);
      setError(undefined);
      setMessages([]);
      setPanel([]);
      setProblemText(utterance);
      append({ id: nextId(), role: 'learner', text: utterance });
      bootstrapping.current = true;
      /**
       * **`problemText` は学習者の最初の発話そのもの**である。
       * §2.2 の 1 は「**問題 + 自分の考え**」を 1 つの入力で受ける。
       */
      const out = await createSession(
        api,
        { problemId: 'adhoc', problemText: utterance, utterance },
        handlers(),
      );
      bootstrapping.current = false;
      setHeld(false);
      setPhase(undefined);
      if (out.kind !== 'complete') {
        /**
         * **何が起きたかを画面に出す**（M16。**実使用で沈黙した**）。
         * **「セッションを作れませんでした」だけでは、
         * 通信断なのかサーバの拒否なのか分からない。**
         */
        setBusy(false);
        setError({
          kind: 'recoverable',
          message:
            out.kind === 'network_error'
              ? `接続できませんでした: ${out.message}`
              : out.kind === 'http_error'
                ? `セッションを作れませんでした（${out.status}）。`
                : `応答が途中で切れました: ${out.reason}`,
        });
      } else {
        void listSessions(api).then(setSessions);
      }
    },
    [api, append, handlers],
  );

  /**
   * **新しい問題を始める**（M16。**実使用で欠けていた**）。
   *
   * **これが無いと、一度セッションができた後の入力はすべて
   * そのセッションのターンとして送られる** — **別の話題を入力すると
   * engine が食い違った意図を組み、T4 が役を降りる**（実際に起きた）。
   *
   * **状態を全部戻す** — `sessionId` だけ消すと、
   * **前の会話とパネルが残ったまま新しい問題が始まる**。
   */
  const startNew = useCallback(() => {
    /** **URL からも外す** — 残すとリロードで前の問題に戻る */
    try {
      history.replaceState({}, '', location.pathname);
    } catch {
      /* 失敗しても新しい問題は始められる */
    }
    setSessionId(undefined);
    setMessages([]);
    setPanel([]);
    setProblemText(undefined);
    setVersion(0);
    setDraft('');
    setError(undefined);
    setHeld(false);
    pendingSubmit.current = undefined;
    bootstrapping.current = false;
    setPhase(undefined);
  }, []);

  /**
   * **履歴から選んだセッションを読み直す**（D12。**専用サマリを生成しない**）。
   *
   * **`sessionId` を差し替えるだけでは会話が出ない**（実使用で欠けていた）。
   * **`GET /api/sessions/{id}` から `EVENT` ログを組み直したものを表示する。**
   */
  const openSession = useCallback(
    (sid: string) => {
      void (async () => {
        const d = await readSession(api, sid);
        if (d === undefined) {
          setError({ kind: 'recoverable', message: 'このセッションを開けませんでした。' });
          return;
        }
        setSessionId(d.sessionId);
        setVersion(d.version);
        setProblemText(d.problemText);
        try {
          history.replaceState({}, '', `?s=${encodeURIComponent(d.sessionId)}`);
        } catch {
          /* 同上 */
        }
        /** **状態ごと復元する**（M16。以前は全件「未確認」に戻していた） */
        setPanel(d.requirements);
        setMessages(
          d.conversation.map((c) => ({
            id: nextId(),
            role: c.role === 'learner' ? ('learner' as const) : ('assistant' as const),
            text: c.text,
          })),
        );
        setError(undefined);
      })();
    },
    [api],
  );

/**
   * **URL にセッションが載っていたら開く**（M16。**リロードで消えないように**）。
   *
   * **一度だけ走らせる** — `openSession` を依存に入れると
   * **`api` が変わるたびに読み直して会話が巻き戻る**。
   */
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    const sid = new URLSearchParams(location.search).get('s');
    if (sid === null) return;
    restored.current = true;
    openSession(sid);
  }, [openSession]);

  const onSubmit = useCallback(() => {
    const text = draft.trim();
    if (text === '') return;
    setDraft('');
    if (sessionId === undefined) {
      /** 1 通目 = セッション作成 */
      if (bootstrapping.current) {
        /**
         * **不可視フェーズ中は保留する**（§2.7。
         * **操作を止めるのではなく結果を遅らせる**）。
         * **ここで初めて「準備中」を出す** — 押す前は通常の見た目である。
         */
        pendingSubmit.current = text;
        setHeld(true);
        /** **押した発話は先に会話へ出す** — 消えたように見せない */
        append({ id: nextId(), role: 'learner', text });
        return;
      }
      /**
       * **投げっぱなしにしない**（M16）。
       * **`void` で捨てると、例外が出たときに画面が沈黙する。**
       */
      void start(text).catch((e: unknown) => {
        setBusy(false);
        setError({ kind: 'fatal', message: `送信できませんでした: ${String(e)}` });
      });
      return;
    }
    void submitTurn(sessionId, text, version).catch((e: unknown) => {
      setBusy(false);
      setError({ kind: 'fatal', message: `送信できませんでした: ${String(e)}` });
    });
  }, [draft, sessionId, version, start, submitTurn]);

  /**
   * **`pending` は「押せるが保留される」**（§2.7）。
   *
   * **`bootstrapping` ではなく `held` を見る** —
   * §2.7 は「**押すと**『準備中』と表示され」と定めており、
   * **押す前は通常の見た目でなければならない**。
   * **不可視フェーズの間ずっと『準備中』を出すのは、
   * 何もしていないのに「何かが保留されている」と広告すること**であり、
   * **§2.2 の隠蔽がそこで壊れる**。
   */
  const composerState: ComposerState = held ? 'pending' : busy ? 'streaming' : 'ready';

  /** **問題名**（モックのスレッドのバー）。1 問 = 1 セッションなので問題文の頭を使う */
  const title =
    problemText === undefined
      ? '新しい問題'
      : problemText.split('\n')[0]?.slice(0, 40) ?? '新しい問題';

  return (
    <AppShell
      brand={<span>Xlabo Chat</span>}
      brandMark={XLABO_SYMBOL}
      title={title}
      barRight={
        <span className={styles['barRight']}>
          <QuotaChip remaining={30} />
          {/* **新しい問題を始める入口**（M16。無いと 2 問目が前の問題のターンになる） */}
          <Button variant="subtle" size="sm" onClick={startNew}>
            <Icon svg={ICONS.add} size={16} />
            新しい問題
          </Button>
        </span>
      }
      rail={
        <SessionList
          sessions={sessions.map((s) => ({
            sessionId: s.sessionId,
            /**
             * **見出しはサーバの `title`**（M16）。
             * **`problemId` は `adhoc` 固定**なので、そのまま出すと
             * **一覧が同じ語の列になる**（実機でそう見えた）。
             * **M16 より前のセッションは `title` を持たない**ので、
             * **そのときだけ「問題」と書く** — `adhoc` は内部語である（§5）。
             */
            problemLabel: s.title ?? '問題',
            createdAt: s.createdAt,
          }))}
          {...(sessionId === undefined ? {} : { selectedId: sessionId })}
          onSelect={openSession}
        />
      }
      panel={<UnderstandingPanel requirements={panel} />}
      composer={
        <>
          <Composer value={draft} onChange={setDraft} onSubmit={onSubmit} state={composerState} />
          {/* **免責は入力欄の下に常設**（D13。**ページ内に 1 箇所だけ**） */}
          <Notice />
        </>
      }
    >
      {messages.length === 0 ? (
        <Text variant="body">
          解けなかった問題を、コードごと貼ってください。どこでつまずいているかを一緒に探します。
        </Text>
      ) : null}

      {messages.map((m, i) =>
        m.role === 'assistant' ? (
          <AssistantUtterance
            key={m.id}
            text={m.text}
            state="done"
            avatarSvg={EKUMARU_AVATAR}
            /**
             * **表情はアプリが決定的に決める**（§4.6 規約 6）。
             * **写像に無い種類は素の顔**（`neutral`）にする —
             * **知らない `actType` に意味を発明しない**。
             */
            character={(m.actType === undefined ? undefined : CHARACTER_BY_ACT[m.actType]) ?? 'neutral'}
            /** **連続発話では名前を繰り返さない**（§4.6 規約 4） */
            showName={messages[i - 1]?.role !== 'assistant'}
          />
        ) : (
          <LearnerBubble key={m.id} text={m.text} state="sent" />
        ),
      )}

      {/*
        * **待っていることの見せ方は 2 つある**（D132）。
        *
        * | | 出すもの | いつ |
        * | --- | --- | --- |
        * | **工程名 + 経過秒** | サーバが `phase` を流したとき | セッション作成（実測 62〜101 秒） |
        * | **点 3 つ** | `phase` が無いとき | 通常ターン（実測 3.8〜8.0 秒） |
        *
        * **短い待ちに工程名を出さない** — 出た瞬間に消えるので読めない。
        * **長い待ちを点 3 つで済ませない** — **62〜101 秒は「何かしている」
        * だけでは足りず、何をしているかと、進んでいることが要る**。
        *
        * **保留中はどちらも出さない** — 保留は Composer が「準備中」で言葉にしている。
        */}
      {phase !== undefined ? (
        <PhaseIndicator
          label={phase.label}
          elapsedSeconds={Math.max(0, Math.floor((now - phase.startedAt) / 1000))}
        />
      ) : busy && !held ? (
        <Thinking svg={EKUMARU_AVATAR} />
      ) : null}

      {/*
        * **§4.4: 保留 15 秒超 →「もう少しかかります」。**
        * **工程名ではない**ので §4.2 に反しない。
        */}
      {waitingLong ? <Text variant="note">もう少しかかります。</Text> : null}

      {error === undefined ? null : (
        <ErrorBanner kind={error.kind} message={error.message} onRetry={() => setError(undefined)} />
      )}
    </AppShell>
  );
}
